import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { uploadProfilePhoto, changeAuthEmail } from '@/services/userProfileService';
import { getUserProfile, updateUserProfile } from '@/services/edgeFunctionWrappers/profilesManagementWrapper';
import { useTranslation } from 'react-i18next';
import { format } from "date-fns";
import { supabase } from '@/integrations/supabase/client';
import { useUserStore } from '@/store/userStore';
import { useStateDraft } from '@/hooks/useStateDraft';

export function useClientDetails(userId: string, refreshUserProfile: () => void, initialData: any = {}) {
  const { t } = useTranslation(["clients", "common"]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: initialData.phone || '',
    address: initialData.address || '',
    emergency_contact: initialData.emergencyContact || '',
    birth_date: initialData.birthDate || '',
    clothing_size: initialData.clothingSize || '',
    notes: initialData.notes || '',
    country: '',
    email: initialData.email || '',
    profile_photo_url: null as string | null,
    bypass_cooldown: false,
    emailChanged: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [lastFetchedUserId, setLastFetchedUserId] = useState<string | null>(null);
  const [lastSyncedUserId, setLastSyncedUserId] = useState<string | null>(null);
  const gymId = useUserStore.getState().gymId;

  // Brouillon persisté par fiche client — restauration silencieuse à la
  // remontée de l'onglet. Les deux effets de synchronisation serveur
  // ci-dessous sont neutralisés quand un brouillon a été restauré (via la
  // ref : le fetch se termine APRÈS la restauration, la closure d'état
  // serait périmée).
  const { hasRestoredDraftRef, clearDraft } = useStateDraft(
    `client-details:${userId}`,
    formData,
    setFormData,
    {
      isEmpty: (d) =>
        !d.first_name && !d.last_name && !d.phone && !d.address &&
        !d.emergency_contact && !d.notes && !d.email,
    }
  );


  // Fetch user profile data with debouncing - only if userId changed
  useEffect(() => {
    // Skip fetch if no userId or if we already fetched this userId
    if (!userId || userId === lastFetchedUserId) {
      if (!userId) {
        console.log("No userId provided to useClientDetails - skipping fetch");
      } else {
        console.log("Skip fetching - already fetched for this userId:", userId);
      }
      
      // If we have no userId, set loading to false
      if (!userId) {
        setIsLoading(false);
      }
      return;
    }
    
    let isMounted = true;
    console.log("useClientDetails - Starting profile fetch for userId:", userId);
    
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        const { data: profile, error } = await getUserProfile(userId);
        
        if (error) throw error;
        
        // Fetch bypass_cooldown from user_gym_assignments
        const { data: gymAssignment } = await supabase
          .from('user_gym_assignments')
          .select('bypass_cooldown')
          .eq('user_id', userId)
          .eq('gym_id', gymId)
          .single();
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        if (profile) {
          console.log("Retrieved user profile:", profile);
          setUserProfile(profile);

          // Un brouillon restauré prime sur la re-synchronisation serveur,
          // MAIS uniquement pour les champs réellement saisis : les champs
          // laissés vides dans le brouillon doivent tout de même recevoir la
          // valeur serveur (sinon un ancien brouillon masque définitivement
          // des données renseignées côté base, ex. contact d'urgence).
          const draftRestored = hasRestoredDraftRef.current;
          const serverValues = {
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            phone: profile.phone || initialData.phone || '',
            address: profile.address || initialData.address || '',
            emergency_contact:
              profile.emergency_contact ||
              profile.emergency_contact_phone ||
              initialData.emergencyContact ||
              '',
            birth_date: profile.birth_date || initialData.birthDate || '',
            clothing_size: profile.clothing_size || initialData.clothingSize || '',
            notes: profile.notes || initialData.notes || '',
            country: profile.country || '',
          } as const;

          setFormData(prevData => {
            const merged: Record<string, unknown> = { ...prevData };
            for (const [field, value] of Object.entries(serverValues)) {
              const current = (prevData as Record<string, unknown>)[field];
              const currentIsEmpty =
                current === undefined || current === null || current === '';
              if (!draftRestored || currentIsEmpty) {
                merged[field] = value;
              }
            }
            merged.profile_photo_url = profile.profile_photo_url || prevData.profile_photo_url || null;
            merged.email = draftRestored && prevData.email ? prevData.email : (initialData.email || '');
            merged.bypass_cooldown = gymAssignment?.bypass_cooldown || false;
            return merged as typeof prevData;
          });

        } else {
          console.error("No profile returned for userId:", userId);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        if (isMounted) {
          toast.error(t('profileFetchError', { ns: 'clients', defaultValue: 'Failed to fetch profile' }));
        }
      } finally {
        if (isMounted) {
          console.log("Setting isLoading to false after fetch for userId:", userId);
          setIsLoading(false);
          setLastFetchedUserId(userId); // Mark this userId as fetched AFTER loading is complete
        }
      }
    };

    fetchUserProfile();
    
    // Cleanup function to prevent state updates after unmounting
    return () => {
      console.log("Cleaning up useClientDetails effect for userId:", userId);
      isMounted = false;
    };
  }, [userId, t, initialData, hasRestoredDraftRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync form data with initialData ONLY when userId actually changes
  // This prevents overwriting user edits during intermediate renders
  useEffect(() => {
    if (!initialData || !userId) return;
    
    // Only sync if this is a new user (different from last synced)
    if (userId === lastSyncedUserId) {
      console.log("useClientDetails - skipping sync, same userId:", userId);
      return;
    }
    
    console.log("useClientDetails - syncing initialData for new user:", userId);
    if (hasRestoredDraftRef.current) {
      setLastSyncedUserId(userId);
      return;
    }
    // Non destructif : ne jamais effacer une valeur déjà chargée (serveur)
    // avec une valeur vide venant de la liste des clients.
    setFormData(prevData => {
      const incoming: Record<string, unknown> = {
        phone: initialData.phone || '',
        address: initialData.address || '',
        emergency_contact: initialData.emergencyContact || '',
        birth_date: initialData.birthDate || '',
        clothing_size: initialData.clothingSize || '',
        notes: initialData.notes || '',
        email: initialData.email || '',
      };
      const merged: Record<string, unknown> = { ...prevData };
      for (const [field, value] of Object.entries(incoming)) {
        const current = (prevData as Record<string, unknown>)[field];
        const currentIsEmpty = current === undefined || current === null || current === '';
        if (value !== '' || currentIsEmpty) merged[field] = value;
      }
      return merged as typeof prevData;
    });

    setLastSyncedUserId(userId);
  }, [initialData, userId, lastSyncedUserId, hasRestoredDraftRef]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    if( name === "email"){
      setFormData(prevData => ({
        ...prevData,
        emailChanged: true
      }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: checked
    }));
  };

  const handleDateChange = (date: Date | undefined) => {
    // Handle both setting a date and clearing it
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      setFormData(prevData => ({
        ...prevData,
        birth_date: formattedDate
      }));
    } else {
      // Clear the birth_date field when date is undefined
      setFormData(prevData => ({
        ...prevData,
        birth_date: ''
      }));
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    setIsUploading(true);
    const file = event.target.files[0];
    
    try {
      // Upload the file and get the URL
      const photoUrl = await uploadProfilePhoto(userId, file);
      
      if (photoUrl) {
        // Update the user profile with the photo URL
        // updateUserProfile renvoie un booléen, pas un { error }.
        const ok = await updateUserProfile(userId, { profile_photo_url: photoUrl }, gymId || undefined);

        if (ok) {
          // Update the local state
          setFormData({
            ...formData,
            profile_photo_url: photoUrl
          });
          
          // Show success message
          toast.success(t('photoUpdatedSuccess', { ns: 'clients', defaultValue: 'Profile photo updated successfully' }));
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(t('photoUploadError', { ns: 'clients', defaultValue: 'Failed to upload photo' }));
    } finally {
      setIsUploading(false);
    }
  };

  
  const updateUser = async (data: any) => {
    console.log("Updating user with data:", data);
    setIsSubmitting(true);
    try {
      // Exclude email and bypass_cooldown from updateUserProfile
      const { email, emailChanged, bypass_cooldown, ...updateData } = data;
      
      // Include the profile_photo_url if it exists
      if (formData.profile_photo_url) {
        updateData.profile_photo_url = formData.profile_photo_url;
      }
      
      // Ensure birth_date is either a valid string or null
      if (updateData.birth_date === '') {
        updateData.birth_date = null;
      }
      
      // Update user_profiles (without bypass_cooldown)
      const ok = await updateUserProfile(userId, updateData, gymId || undefined);

      if (!ok) {
        toast.error(t('updateError', { ns: 'clients', defaultValue: 'Failed to update profile' }));
        return;
      }

      // Update bypass_cooldown in user_gym_assignments
      const { error: bypassError } = await supabase
        .from('user_gym_assignments')
        .update({ bypass_cooldown })
        .eq('user_id', userId)
        .eq('gym_id', gymId);

      if (bypassError) {
        console.error('Error updating bypass_cooldown:', bypassError);
        toast.error(t('updateError', { ns: 'clients', defaultValue: 'Failed to update profile' }));
        return;
      }

      // Update email if changed
      if (formData.emailChanged) {
        console.log("email changed")
        await changeAuthEmail(userId, email);
        toast.success(t('updateSuccessWithEmail', { ns: 'clients', defaultValue: 'Profile updated successfully' }));
      } else {
        toast.success(t('updateSuccess', { ns: 'clients', defaultValue: 'Profile updated successfully' }));
      }

      clearDraft();
      refreshUserProfile(); // Refresh the profile data
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(t('updateError', { ns: 'clients', defaultValue: 'Failed to update profile' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateUser(formData);
  };

  // Export all the necessary functions and states
  return {
    formData,
    setFormData,
    isSubmitting,
    isUploading,
    isLoading,
    userProfile,
    handleChange,
    handleSwitchChange,
    handleDateChange,
    handleFileUpload,
    handleSubmit
  };
}


